/**
 * GroupTactics — pure, Phaser-free.
 *
 * Positioning helper for mob gang behaviour (MY-1887):
 *   - computeSurroundSlot  : spread ≥3 attackers to flanks + behind the hero.
 *   - shouldRetreat        : lone low-HP mob backs off rather than feeding.
 *
 * The Phaser adapter (Enemy.ts) calls these to produce the orbit offsets it
 * passes to tickEnemyAI; the AI itself just follows the offsets.
 */

/** Radius around the hero for a surround formation (px, world-space). */
const SURROUND_RADIUS = 100;

/** 2.5-D Y squash so the circle reads naturally in the beat-em-up perspective. */
const SURROUND_Y_SQUASH = 0.45;

/** HP fraction at or below which a lone mob will retreat rather than feed. */
export const RETREAT_HP_RATIO = 0.25;

/** Minimum attacker count before the surround pattern activates. */
export const SURROUND_MIN_ATTACKERS = 3;

/** Distance the retreating mob backs off to before it re-engages (px). */
export const RETREAT_SAFE_DIST = 260;

export interface SurroundSlot {
  /** X offset from hero center (world-space). */
  offsetX: number;
  /** Y offset from hero center (world-space). */
  offsetY: number;
}

/**
 * Evenly distribute `total` attackers around the hero and return the slot
 * for the attacker at `index`.
 *
 * Distinct from computeOrbitOffset: it uses a dedicated radius and is the
 * named entry-point for the ≥3-mob surround behaviour.
 */
export function computeSurroundSlot(index: number, total: number): SurroundSlot {
  const n = Math.max(total, 1);
  const angle = (2 * Math.PI * index) / n;
  return {
    offsetX: Math.cos(angle) * SURROUND_RADIUS,
    offsetY: Math.sin(angle) * SURROUND_RADIUS * SURROUND_Y_SQUASH,
  };
}

export interface RetreatInput {
  currentHp: number;
  maxHp: number;
  /** Total live attackers engaging this hero from the enemy group. */
  attackerCount: number;
}

/**
 * Returns true when a lone low-HP mob should back off instead of feeding.
 * Triggers only when: alone (attackerCount === 1) AND HP ≤ RETREAT_HP_RATIO.
 */
export function shouldRetreat(input: RetreatInput): boolean {
  if (input.attackerCount !== 1) return false;
  if (input.maxHp <= 0) return false;
  return input.currentHp / input.maxHp <= RETREAT_HP_RATIO;
}
